import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  onChange: (step: number) => void;
}

export function StepIndicator({
  currentStep,
  totalSteps,
  onChange,
}: StepIndicatorProps) {
  const { t, i18n } = useTranslation();
  
  // Create a mapping of step titles
  const stepTitles = {
    1: t("steps.pumpInfo"),
    2: t("steps.application"),
    3: t("steps.loadProfile"),
    4: t("steps.operation"),
    5: t("steps.electrical"),
    6: t("steps.utility"),
    7: t("steps.facility"),
    8: t("steps.controls"),
    9: t("steps.retrofit"),
    10: t("steps.documents"),
    11: t("steps.analytics")
  };
  
  // Access the translation directly from resources for debugging
  const translationTest = i18n.language && 
    i18n.options && 
    i18n.options.resources && 
    i18n.options.resources[i18n.language] && 
    i18n.options.resources[i18n.language].translation;
    
  // For debugging - check if we can access translations directly
  const canAccessTranslations = !!translationTest;
  
  // Create array of step numbers
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  
  return (
    <div className="step-indicator">
      {steps.map((step) => (
        <div key={step} className="step-indicator-item">
          <button
            type="button"
            className={cn(
              "step-indicator-button",
              step === currentStep && "active",
              step < currentStep && "completed"
            )}
            onClick={() => onChange(step)}
          >
            <div
              className="step-indicator-number"
            >
              {step < currentStep ? (
                <Check className="h-4 w-4" />
              ) : (
                step
              )}
            </div>
            <span className="step-indicator-text">
              {/* First use direct text as fallback, then try to translate */}
              {stepTitles[step as keyof typeof stepTitles] || `Step ${step}`}
              
              {/* Add a hidden debug element to check translation access */}
              {step === 1 && (
                <span className="sr-only">
                  Translation access: {canAccessTranslations ? 'Yes' : 'No'}
                </span>
              )}
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}
